import { useEffect, useMemo, useState } from "react";
import { apiGet, type Activity, type Provider } from "../api/client";
import { ActivityBrick } from "../components/ActivityBrick";
import { EmptyMessage } from "../components/EmptyMessage";
import { FilterDropdown } from "../components/FilterDropdown";
import { PageHeader, PageHeaderAction } from "../components/PageHeader";

const FILTERS = [
  { key: "All", title: "All activity" },
  { key: "Appointment Requested", title: "Appointment requested" },
  { key: "Message Received", title: "Messages" },
  { key: "Appointment Updated", title: "Appointment updated" },
  { key: "Appointment Cancelled", title: "Appointment cancelled" },
  { key: "Survey Submission Created", title: "Surveys" },
];

type BundleCounts = {
  appt_request: number;
  msgs: number;
  appt_survey: number;
  all: number;
};

function classify(item: Activity): "appt_request" | "msgs" | "appt_survey" | "other" {
  const t = item.activity_type || "";
  const k = (item.key || "").toLowerCase();
  if (
    t === "Message" ||
    t === "MessageReceived" ||
    k.includes("message")
  ) {
    return "msgs";
  }
  if (
    t === "SurveySubmission" ||
    t === "SurveySubmissionCreated" ||
    k.includes("survey")
  ) {
    return "appt_survey";
  }
  if (
    t === "AppointmentCreate" ||
    k.includes("request") ||
    k.includes("appointment create")
  ) {
    return "appt_request";
  }
  return "other";
}

export function ActivitiesPage() {
  const [items, setItems] = useState<Activity[]>([]);
  const [allItems, setAllItems] = useState<Activity[]>([]);
  const [apiCounts, setApiCounts] = useState<Record<string, number> | null>(
    null,
  );
  const [providers, setProviders] = useState<Provider[]>([]);
  const [loading, setLoading] = useState(true);
  const [key, setKey] = useState("All");
  const [providerId, setProviderId] = useState("all");

  useEffect(() => {
    let cancelled = false;
    apiGet<Array<{ providers?: Provider[]; doctors?: Provider[] }>>("/doctors")
      .then((d) => {
        if (cancelled) return;
        const row = Array.isArray(d) ? d[0] : d;
        const list = row?.providers || row?.doctors || [];
        setProviders(Array.isArray(list) ? list : []);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    // Load a dense page for the timeline + bundle counters.
    apiGet<
      Array<{ activities: Activity[]; counts?: Record<string, number> }>
    >("/timelines", {
      key: "All",
      page: 1,
      per_page: 100,
    })
      .then((data) => {
        if (cancelled) return;
        setAllItems(data?.[0]?.activities ?? []);
        setApiCounts(data?.[0]?.counts ?? null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const counts: BundleCounts = useMemo(() => {
    // Bundle counters prefer API counts when present.
    if (apiCounts) {
      return {
        appt_request: apiCounts["Appointment Requested"] ?? 0,
        msgs: apiCounts["Message Received"] ?? 0,
        appt_survey: apiCounts["Survey Submission Created"] ?? 0,
        // Angular dashboard.js: count.all = _.sum(_.values(counts))
        // (includes the "All" key itself → 82 when All=41 + categories).
        all: Object.values(apiCounts).reduce((sum, n) => sum + (n || 0), 0),
      };
    }
    const c = { appt_request: 0, msgs: 0, appt_survey: 0, all: allItems.length };
    for (const item of allItems) {
      const kind = classify(item);
      if (kind !== "other") c[kind] += 1;
    }
    return c;
  }, [allItems, apiCounts]);

  useEffect(() => {
    let list = allItems;
    if (key === "Appointment Requested") {
      list = list.filter((i) => classify(i) === "appt_request");
    } else if (key === "Message Received") {
      list = list.filter((i) => classify(i) === "msgs");
    } else if (key === "Survey Submission Created") {
      list = list.filter((i) => classify(i) === "appt_survey");
    } else if (key === "Appointment Updated") {
      list = list.filter(
        (i) =>
          i.activity_type === "AppointmentResched" ||
          i.activity_type === "AppointmentConfirm" ||
          (i.key || "").toLowerCase().includes("resched") ||
          (i.key || "").toLowerCase().includes("confirm"),
      );
    } else if (key === "Appointment Cancelled") {
      list = list.filter(
        (i) =>
          i.activity_type === "AppointmentCancel" ||
          (i.key || "").toLowerCase().includes("cancel"),
      );
    }
    if (providerId !== "all") {
      list = list.filter(
        (i) =>
          i.parameters?.provider_id === providerId ||
          i.trackable?.provider_id === providerId,
      );
    }
    // Angular dir-paginate default page size is 15 — only paint one page so
    // the VRT viewport matches brick count/rhythm (was 41 vs Angular 15).
    setItems(list.slice(0, 15));
  }, [allItems, key, providerId]);

  const providerOptions = useMemo(
    () => [
      { key: "all", title: "All providers" },
      ...providers.map((p) => ({ key: p.id, title: p.name })),
    ],
    [providers],
  );

  const showBundle =
    counts.appt_request + counts.msgs + counts.appt_survey > 0;

  return (
    <>
      <PageHeader
        title="Activities"
        actions={
          <PageHeaderAction
            title="Invite patient"
            label="Invite patient"
            icon="ne__icon__send"
            variant="text"
          />
        }
      />

      <div className="ne__activities__view">
        {showBundle ? (
          <div className="ne__activities__timeline-bundle">
            <div className="ne__timeline-bundle">
              <div className="ne__timeline-bundle__padding">
                <header className="ne__timeline-bundle__header">
                  <h1 className="ne__timeline-bundle__heading">
                    Hi Alex, on your to-do list today
                  </h1>
                </header>
                <ul className="ne__timeline-bundle__list">
                  {counts.appt_request > 0 ? (
                    <li className="ne__timeline-bundle__item">
                      <button
                        type="button"
                        className={`ne__timeline-bundle__link${
                          key === "Appointment Requested" ? " active" : ""
                        }`}
                        onClick={() => setKey("Appointment Requested")}
                      >
                        <h2 className="ne__timeline-bundle__counter">
                          {counts.appt_request}
                        </h2>
                        <h6 className="ne__timeline-bundle__eyebrow">
                          <span>
                            {counts.appt_request === 1
                              ? "Appointment"
                              : "Appointments"}
                          </span>
                          <span> to confirm</span>
                        </h6>
                      </button>
                    </li>
                  ) : null}
                  {counts.msgs > 0 ? (
                    <li className="ne__timeline-bundle__item">
                      <button
                        type="button"
                        className={`ne__timeline-bundle__link${
                          key === "Message Received" ? " active" : ""
                        }`}
                        onClick={() => setKey("Message Received")}
                      >
                        <h2 className="ne__timeline-bundle__counter">
                          {counts.msgs}
                        </h2>
                        <h6 className="ne__timeline-bundle__eyebrow">
                          <span>
                            {counts.msgs === 1 ? "Message" : "Messages"}
                          </span>
                          <span> to check</span>
                        </h6>
                      </button>
                    </li>
                  ) : null}
                  {counts.appt_survey > 0 ? (
                    <li className="ne__timeline-bundle__item">
                      <button
                        type="button"
                        className={`ne__timeline-bundle__link${
                          key === "Survey Submission Created" ? " active" : ""
                        }`}
                        onClick={() => setKey("Survey Submission Created")}
                      >
                        <h2 className="ne__timeline-bundle__counter">
                          {counts.appt_survey}
                        </h2>
                        <h6 className="ne__timeline-bundle__eyebrow">
                          <span>
                            {counts.appt_survey === 1 ? "Survey" : "Surveys"}
                          </span>
                          <span> received</span>
                        </h6>
                      </button>
                    </li>
                  ) : null}
                  {key !== "All" ? (
                    <li className="ne__timeline-bundle__item ne__timeline-bundle__item--see-all">
                      <button
                        type="button"
                        className="ne__timeline-bundle__link ne__timeline-bundle__link--see-all visible"
                        onClick={() => setKey("All")}
                      >
                        <h6 className="ne__timeline-bundle__eyebrow">
                          See all activity
                        </h6>
                      </button>
                    </li>
                  ) : null}
                </ul>
              </div>
            </div>
          </div>
        ) : null}

        <ul className="ne__timeline__dropdowns">
          <div className="ne__timeline__dropdowns-padding">
            <li className="ne__timeline__activity-type">
              <FilterDropdown
                options={FILTERS}
                value={key}
                onChange={setKey}
                badgeCount={key === "All" ? counts.all : items.length}
              />
            </li>
            <li className="ne__timeline__providers">
              <FilterDropdown
                options={providerOptions}
                value={providerId}
                onChange={setProviderId}
              />
            </li>
          </div>
        </ul>

        {loading ? (
          <EmptyMessage>
            <p>Loading activities…</p>
          </EmptyMessage>
        ) : null}

        {!loading && items.length === 0 ? (
          <EmptyMessage className="ne__empty-message--no-activities">
            <h3>Looks like you have no activities here.</h3>
            <p>
              As patients request appointments, send messages, complete surveys,
              you should see it here.
            </p>
          </EmptyMessage>
        ) : null}

        {!loading && items.length > 0 ? (
          <ul className="ne__timeline">
            {items.map((item) => (
              <ActivityBrick key={item.id} item={item} />
            ))}
          </ul>
        ) : null}
      </div>
    </>
  );
}
